import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppState } from '../reduxish/appState';

interface PollingState {
    isRefreshing: boolean,
    lastUpdated: Date | null,
    refreshNow: () => void,
}

/**
 * Periodically calls `fetcher` to keep some data fresh. Skips the initial call
 * (assumes the caller already loaded on mount), pauses while the tab is hidden,
 * and fires an immediate refresh when the tab becomes visible if data is stale.
 *
 * The interval comes from `clientConfig.minBetRefreshIntervalSeconds`. Pass
 * `enabled=false` to suspend polling (e.g. while a slug is unresolved).
 */
export default function usePollingRefresh(
    fetcher: (signal: AbortSignal) => Promise<void>,
    enabled: boolean = true,
): PollingState {
    const { state: { clientConfig } } = useAppState();
    const intervalSec = clientConfig?.minBetRefreshIntervalSeconds ?? 300;
    const intervalMs = intervalSec * 1000;

    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;
    const inFlightRef = useRef(false);
    const lastUpdatedRef = useRef<Date | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const mountedRef = useRef(true);

    const run = useCallback(async () => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        setIsRefreshing(true);
        try {
            await fetcherRef.current(ctrl.signal);
            if (!mountedRef.current || ctrl.signal.aborted) return;
            const now = new Date();
            lastUpdatedRef.current = now;
            setLastUpdated(now);
        } catch {
            // Swallow; caller's fetcher handles its own error UX.
        } finally {
            inFlightRef.current = false;
            if (mountedRef.current) setIsRefreshing(false);
        }
    }, []);

    const refreshNow = useCallback(() => { void run(); }, [run]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            abortRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        if (!enabled) return;

        const id = window.setInterval(() => {
            if (document.hidden) return;
            void run();
        }, intervalMs);

        const onVisible = () => {
            if (document.hidden) return;
            const last = lastUpdatedRef.current;
            if (!last || Date.now() - last.getTime() >= intervalMs) {
                void run();
            }
        };
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            window.clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [enabled, intervalMs, run]);

    return { isRefreshing, lastUpdated, refreshNow };
}
